import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import {
  AbstractControl,
  NgControl,
  ValidationErrors,
  Validator,
  ValidatorFn,
} from '@angular/forms';
import {
  ControlsOf,
  ControlValueAccessor,
  FormArray,
  FormGroup,
  ValuesOf,
} from '@ngneat/reactive-forms';
import { Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'digital-platform-nested-form',
  templateUrl: './nested-form.component.html',
  styleUrls: ['./nested-form.component.css'],
})
export class NestedFormComponent<
  T extends Record<string, any> = Record<string, any>,
  R extends Record<string, any> = T
> implements OnInit, OnDestroy, Validator, ControlValueAccessor<T>
{
  public form: FormGroup<ControlsOf<R>>;
  public value: T;
  public readonly DEFAULT_VALUE: R;

  protected changes$ = new Subject<T>();
  protected touch$ = new Subject<boolean>();
  protected destroyed$ = new Subject<void>();

  private originalValidators: ValidatorFn[] = [];

  constructor(@Optional() public controlDir: NgControl) {
    if (this.controlDir !== null) {
      this.controlDir.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    if (!this.form) {
      // this should never actually be needed, as this is meant to be used as a base class
      this.form = new FormGroup<R>({} as any);
      console.warn(
        'Direct usage of NestedFormComponent is unsupported, please extend your own form component with this class instead.'
      );
    }

    this.form.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        startWith(this.form.value),
        map((value) => this.mapOutputValue(value))
      )
      .subscribe((changes) => this.changes$.next(changes));

    this.form.touch$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((touch) => this.touch$.next(touch));

    if (this.controlDir !== null) {
      const control = this.controlDir.control;
      if (control) {
        let myValidators = [this.validate];
        if (control?.validator) {
          this.originalValidators.push(control.validator);
          myValidators = [control.validator, ...myValidators];
        }
        control?.setValidators(myValidators);
        control?.updateValueAndValidity();
        const markAllAsTouched = control?.markAllAsTouched.bind(control);
        control.markAllAsTouched = () => {
          markAllAsTouched();
          this.form.markAllAsTouched();
        };
      }
    }
  }

  public ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();

    // restore validators
    if (this.controlDir && this.originalValidators) {
      this.controlDir?.control?.setValidators(this.originalValidators);
    }
  }

  // override this to modify the value that the form control returns
  // e.g. for a condition that should return null
  public mapOutputValue(value: ValuesOf<ControlsOf<R>>): T {
    return (<unknown>value) as T;
  }

  // override this to modify the value that the form control is passed in
  public mapInputValue(value: T): ValuesOf<ControlsOf<R>> {
    return (<unknown>value) as R;
  }

  // we use an arrow function here to maintain `this` scope to the component
  validate: ValidatorFn = (c) => {
    this.form.updateValueAndValidity({
      onlySelf: false,
      emitEvent: false,
    });

    if (this.form.valid) {
      return null;
    }

    const errors = this.getAllErrors(this.form);

    return errors;
  };

  // recursively aggregate all form errors
  private getAllErrors(
    formObj: FormGroup<any> | FormArray<any> | AbstractControl,
    prefix = ''
  ): ValidationErrors | null {
    if (formObj instanceof FormGroup) {
      return Object.keys(formObj.controls).reduce(
        (acc, key) => ({
          ...acc,
          ...this.getAllErrors(
            formObj.get(key),
            `${prefix !== '' ? prefix + '.' : ''}${key}`
          ),
        }),
        {}
      );
    }

    if (formObj instanceof FormArray) {
      return Array.prototype.reduce.call(
        formObj.controls,
        (
          acc: ValidationErrors,
          control: FormGroup<any> | FormArray<any> | AbstractControl,
          i: number
        ): ValidationErrors => ({
          ...acc,
          ...this.getAllErrors(
            control,
            `${prefix !== '' ? prefix + '.' : ''}${i}`
          ),
        }),
        {} as ValidationErrors
      );
    }

    if (formObj.errors != null) {
      return Object.keys(formObj.errors).reduce((accError, keyError) => {
        const nestedKey = `${prefix}.${keyError}`;

        // workaround for a known MatDatepicker bug
        // @see https://github.com/angular/components/issues/11523
        if (keyError === 'matDatepickerParse') {
          return accError;
        }

        return {
          ...accError,
          [nestedKey]: formObj.errors[keyError],
        };
      }, {});
    }

    return null;
  }

  public writeValue(value: T) {
    this.value = value;
    if (this.form) {
      if (value) {
        this.form.patchValue(this.mapInputValue(value));
      } else {
        if (this.DEFAULT_VALUE) {
          this.form.reset(this.DEFAULT_VALUE);
        } else {
          this.form.reset();
        }
      }
    }
  }

  public registerOnChange(fn: (value: T) => void) {
    this.changes$.pipe(takeUntil(this.destroyed$)).subscribe(fn);
  }

  public onTouched = () => {};
  public registerOnTouched(fn: () => void) {
    this.touch$.pipe(takeUntil(this.destroyed$)).subscribe(fn);
  }

  setDisabledState(isDisabled: boolean) {
    if (this.form) {
      if (isDisabled) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }
  }
}
